/**
 * AttestationsSection
 *
 * Conditional attestation checkboxes: telehealth consent, location confirmed,
 * PDMP reviewed. Visibility is frontend-owned based on modality and patient.
 */

import React from 'react';
import {
  CheckCircle as CheckCircleIcon,
  LocalPharmacy as PdmpIcon,
  Person as PersonIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { alpha, Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import type { EncounterAttestations, Modality } from '../../types/attestations';

interface AttestationsSectionProps {
  modality: Modality | null;
  attestations: Partial<EncounterAttestations>;
  onAttestationChange: (
    field: keyof EncounterAttestations,
    value: boolean | number | null,
  ) => void;
  hasPatient?: boolean;
  editable?: boolean;
}

interface AttestationRowProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
  editable: boolean;
}

const AttestationRow: React.FC<AttestationRowProps> = ({
  icon,
  label,
  description,
  checked,
  onToggle,
  editable,
}) => {
  const theme = useTheme();

  return (
    <Tooltip title={checked ? `Click to uncheck ${label.toLowerCase()}` : `Click to confirm ${label.toLowerCase()}`}>
      <Box
        onClick={editable ? onToggle : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          cursor: editable ? 'pointer' : 'default',
          transition: 'background-color 0.2s',
          '&:hover': editable ? { bgcolor: 'action.hover' } : undefined,
          borderRadius: 1,
          px: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: checked
              ? alpha(theme.palette.success.main, 0.1)
              : alpha(theme.palette.grey[500], 0.08),
            color: checked
              ? theme.palette.success.main
              : theme.palette.text.disabled,
            transition: 'all 0.2s',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}
          >
            {label}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.2 }}
          >
            {description}
          </Typography>
        </Box>
        {checked ? (
          <CheckCircleIcon
            sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }}
          />
        ) : (
          <UncheckedIcon
            sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

export const AttestationsSection: React.FC<AttestationsSectionProps> = ({
  modality,
  attestations,
  onAttestationChange,
  hasPatient = false,
  editable = true,
}) => {
  const { encounter: encounterTracking } = useEventTracking();

  const isTelehealth = modality === 'video' || modality === 'audio';
  const showTelehealthConsent = isTelehealth;
  const showLocationConfirmed = isTelehealth;
  const showPdmp = hasPatient;

  const hasVisibleItems = showTelehealthConsent || showLocationConfirmed || showPdmp;

  if (!hasVisibleItems) return null;

  return (
    <Stack spacing={0}>
      {showTelehealthConsent && (
        <AttestationRow
          icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
          label="Telehealth consent"
          description={
            attestations.telehealth_consent
              ? 'Consent obtained'
              : 'Click to confirm'
          }
          checked={!!attestations.telehealth_consent}
          onToggle={() => {
            const newValue = !attestations.telehealth_consent;
            encounterTracking.attestationToggled({ attestation_type: 'telehealth_consent', checked: newValue });
            onAttestationChange('telehealth_consent', newValue);
          }}
          editable={editable}
        />
      )}

      {showLocationConfirmed && (
        <AttestationRow
          icon={<PersonIcon sx={{ fontSize: 14 }} />}
          label="Location confirmed"
          description={
            attestations.location_confirmed
              ? 'Location verified'
              : 'Click to confirm'
          }
          checked={!!attestations.location_confirmed}
          onToggle={() => {
            const newValue = !attestations.location_confirmed;
            encounterTracking.attestationToggled({ attestation_type: 'location_confirmed', checked: newValue });
            onAttestationChange('location_confirmed', newValue);
          }}
          editable={editable}
        />
      )}

      {showPdmp && (
        <AttestationRow
          icon={<PdmpIcon sx={{ fontSize: 14 }} />}
          label="PDMP reviewed"
          description={
            attestations.pdmp_reviewed
              ? 'Prescription monitoring verified'
              : 'Click to confirm'
          }
          checked={!!attestations.pdmp_reviewed}
          onToggle={() => {
            const newValue = !attestations.pdmp_reviewed;
            encounterTracking.attestationToggled({ attestation_type: 'pdmp_reviewed', checked: newValue });
            onAttestationChange('pdmp_reviewed', newValue);
          }}
          editable={editable}
        />
      )}
    </Stack>
  );
};
